import { checkinsApi, type CheckinRequest } from './api';
import { authApi } from './api/auth';

interface QueuedCheckin {
  id: string;
  eventId: string;
  data: CheckinRequest;
  timestamp: number;
  // #3688: the member who queued it. The sync uploads an item only while this
  // member is signed in. Optional because items queued before #3688 have none.
  userId?: string;
}

const STORAGE_KEY = 'oro_checkin_queue';

// Get queued check-ins from localStorage
export function getQueuedCheckins(): QueuedCheckin[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load queued check-ins:', error);
    return [];
  }
}

// Save queued check-ins to localStorage
function saveQueuedCheckins(queue: QueuedCheckin[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Failed to save queued check-ins:', error);
  }
}

// Add a check-in to the queue, owned by the signed-in member (#3688)
export function queueCheckin(eventId: string, data: CheckinRequest, userId: string): string {
  const queue = getQueuedCheckins();
  const queuedCheckin: QueuedCheckin = {
    id: crypto.randomUUID(),
    eventId,
    data: { ...data, source: 'offline' }, // Force offline source
    timestamp: Date.now(),
    userId,
  };
  
  queue.push(queuedCheckin);
  saveQueuedCheckins(queue);
  // #3748: a flush already running may have read the queue before this item.
  if (inFlightSync) followUpRequested = true;

  return queuedCheckin.id;
}

// #3678: the queue id of this member's pending check-in for this event (the
// resolved event UUID, Rule 50), so CheckinButton can restore its "Queued for
// Sync" card after a remount. Another member's item, or one with no owner,
// never matches.
export function findQueuedCheckinId(eventId: string, userId: string): string | null {
  const match = getQueuedCheckins().find(
    (item) => item.eventId === eventId && item.userId === userId,
  );
  return match ? match.id : null;
}

// Remove a check-in from the queue
function removeFromQueue(id: string): void {
  const queue = getQueuedCheckins();
  const filtered = queue.filter((item) => item.id !== id);
  saveQueuedCheckins(filtered);
}

// #3688: called on every sign-out (lib/auth logout and the two account
// deletion confirmations, but not when another account keeps the session,
// #3881), so the next person on the device never inherits it.
export function clearQueuedCheckins(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear queued check-ins:', error);
  }
}

export interface SyncedCheckin {
  queueId: string;
  eventId: string;
}

export interface SyncResult {
  success: number;
  failed: number;
  errors: Array<{ eventId: string; error: string }>;
  // #3598: the items that uploaded, so callers can refresh exactly the events
  // they touched instead of every cached event query.
  synced: SyncedCheckin[];
}

// #3604: per-item notification for UI waiting on one queued check-in
// (CheckinButton's "Queued for Sync" card). Plain listeners, no React here.
type SyncedListener = (item: SyncedCheckin) => void;
const syncedListeners = new Set<SyncedListener>();

export function onCheckinSynced(listener: SyncedListener): () => void {
  syncedListeners.add(listener);
  return () => {
    syncedListeners.delete(listener);
  };
}

function notifySynced(item: SyncedCheckin): void {
  for (const listener of syncedListeners) {
    try {
      listener(item);
    } catch (error) {
      console.error('Check-in sync listener failed:', error);
    }
  }
}

// #3688: who the session cookie belongs to, per the server. createCheckin rides
// the cookie, so this (not a tab's cached user, which can be stale while
// another tab has signed someone else in) is the identity an upload must match.
// Fails closed: an error or no session returns null.
async function confirmSessionUserId(): Promise<string | null> {
  try {
    const me = await authApi.me();
    return me?.id ?? null;
  } catch (error) {
    console.warn('Could not confirm the session owner; keeping queued check-ins:', error);
    return null;
  }
}

// Sync the session owner's queued check-ins with the server.
//
// #3688: an item uploads only when its owner is the member the server says the
// session belongs to, confirmed again before every upload so a session change
// during the flush stops the remaining items. If the server cannot confirm a
// member, nothing uploads and nothing is dropped. Items owned by someone else
// stay queued for their owner. Items with no owner (queued before #3688) can
// never be attributed, so they are dropped without uploading. The race
// between the identity check and the POST is closed by the server: the upload
// carries expected_user_id and the check-in handler refuses a mismatch (#3698).
//
// #3734: one flush at a time. A caller that arrives while a flush is running
// (the mount flush overlapping an `online` event, a StrictMode double effect)
// gets that run's promise instead of starting a second one, so no item is
// read twice, POSTed twice, or announced twice.
//
// #3748: the running pass may already have read the queue, so a caller that
// joins it, or a queueCheckin during it, asks for a follow-up pass. A follow-up
// runs only when an item this run could upload arrived after the pass read the
// queue: one owned by the member the pass confirmed as the session owner. A
// joiner adds no item, so a stream of joiners cannot spin the run (with an
// unconfirmed session, still one identity call), and another member's item
// cannot force a pass. MAX_SYNC_PASSES bounds a listener that queues again
// from onCheckinSynced. The joined promise resolves to the totals of every
// pass; an item that failed and then uploaded in a later pass counts as synced
// only.
//
// The slot is taken before the first pass can reach the identity call, and is
// released in the same synchronous step as the decision to stop, so a caller
// arriving after that decision starts a fresh run instead of joining one that
// will not look at its item.
const MAX_SYNC_PASSES = 32;
let inFlightSync: Promise<SyncResult> | null = null;
let followUpRequested = false;

export function syncQueuedCheckins(): Promise<SyncResult> {
  if (inFlightSync) {
    followUpRequested = true;
    return inFlightSync;
  }
  let resolveRun!: (result: SyncResult) => void;
  let rejectRun!: (error: unknown) => void;
  const run = new Promise<SyncResult>((resolve, reject) => {
    resolveRun = resolve;
    rejectRun = reject;
  });
  inFlightSync = run;
  runSyncPasses().then(resolveRun, rejectRun);
  return run;
}

async function runSyncPasses(): Promise<SyncResult> {
  const synced: SyncedCheckin[] = [];
  // Keyed by queue id: the latest failure of each item that has not uploaded.
  const failures = new Map<string, { eventId: string; error: string }>();

  try {
    for (let passes = 0; passes < MAX_SYNC_PASSES; passes++) {
      followUpRequested = false;
      const pass = await flushQueuedCheckins();
      for (const item of pass.synced) {
        synced.push(item);
        failures.delete(item.queueId);
      }
      for (const [queueId, failure] of pass.failures) failures.set(queueId, failure);

      if (!followUpRequested) break;
      const arrived = getQueuedCheckins().some(
        (item) =>
          !pass.readIds.has(item.id) &&
          pass.sessionUserId !== null &&
          item.userId === pass.sessionUserId,
      );
      if (!arrived) break;
    }
  } finally {
    // No await between the last decision above and this release. Only this
    // block clears the slot, and only the run holding it gets here, so it
    // cannot clear a newer run. The flag is reset at the start of every pass.
    inFlightSync = null;
  }

  return {
    success: synced.length,
    failed: failures.size,
    errors: [...failures.values()],
    synced,
  };
}

interface PassResult {
  synced: SyncedCheckin[];
  failures: Map<string, { eventId: string; error: string }>;
  // The ids this pass read. It reads the queue once, so an id outside this set
  // arrived during the pass and was never attempted by it.
  readIds: Set<string>;
  // The last session owner the server confirmed during the pass, or null.
  sessionUserId: string | null;
}

async function flushQueuedCheckins(): Promise<PassResult> {
  const queue = getQueuedCheckins();
  const synced: SyncedCheckin[] = [];
  const failures: PassResult['failures'] = new Map();
  const readIds = new Set(queue.map((item) => item.id));
  let sessionUserId: string | null = null;

  for (const item of queue) {
    if (!item.userId) {
      console.warn('Dropping a queued check-in with no owner (queued before #3688)');
      removeFromQueue(item.id);
      continue;
    }
    // A null session (unconfirmed) never equals an owner, so it uploads nothing.
    sessionUserId = await confirmSessionUserId();
    if (item.userId !== sessionUserId) continue;

    try {
      // #3698: name the owner so the server refuses the upload (403) if the
      // session changed after the identity check. A refusal lands in the catch
      // below like any other error: counted as failed, left queued for its
      // owner, retried on a later pass of this run (#3748) or the next reconnect.
      await checkinsApi.createCheckin(item.eventId, {
        ...item.data,
        expected_user_id: item.userId,
      });
      removeFromQueue(item.id);
      const syncedItem = { queueId: item.id, eventId: item.eventId };
      synced.push(syncedItem);
      notifySynced(syncedItem);
    } catch (error) {
      failures.set(item.id, {
        eventId: item.eventId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  return { synced, failures, readIds, sessionUserId };
}

// Check if browser is online
export function isOnline(): boolean {
  return navigator.onLine;
}

// Setup online/offline event listeners
export function setupOfflineSync(onSync?: (result: SyncResult) => void): () => void {
  // #3749: the setup flush and an `online` event can join one shared run, and
  // both get the same promise. Hand each run's result to onSync once, and not
  // after cleanup (StrictMode's first setup), so the surviving registration
  // gets it exactly once.
  let lastDelivered: Promise<SyncResult> | null = null;
  let active = true;
  const handleOnline = async () => {
    console.log('Browser is online, syncing queued check-ins...');
    const run = syncQueuedCheckins();
    const result = await run;
    if (!active || run === lastDelivered) return;
    lastDelivered = run;

    if (result.success > 0 || result.failed > 0) {
      console.log(`Sync complete: ${result.success} success, ${result.failed} failed`);
      if (onSync) {
        onSync(result);
      }
    }
  };

  window.addEventListener('online', handleOnline);

  // #3678: a check-in queued before the app was closed would otherwise wait
  // for the next offline-to-online transition. If the app starts online, flush
  // now through the same path.
  if (isOnline()) {
    void handleOnline();
  }

  // Cleanup function
  return () => {
    active = false;
    window.removeEventListener('online', handleOnline);
  };
}

// #3733: count of this member's queued check-ins, for the offline badge. On a
// shared device another member's items, and items with no owner, are not
// counted (the same match as findQueuedCheckinId). No member, no count.
export function getQueuedCount(userId: string | null | undefined): number {
  if (!userId) return 0;
  return getQueuedCheckins().filter((item) => item.userId === userId).length;
}
